import React, { useMemo, useState } from 'react';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupIcon from '@mui/icons-material/Group';
import CloudIcon from '@mui/icons-material/Cloud';
import PanToolIcon from '@mui/icons-material/PanTool';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PageTitle from '../../components/PageTitle';
import MUIModal from '../../components/MuiModal';
import Loader from '../../common/Loader';
import KpiCard from './shared/KpiCard';
import PillFilters from './shared/PillFilters';
import FilterBar from './shared/FilterBar';
import {
    PILL_KEYS, CLOSURE_CATEGORIES, buildPillFilter, buildOverdueFilter, getDefaultDateRange, computeTotalMissed,
    getClosureCategoryMeta, WAITING_FIRST_APPOINTMENT_FILTER,
} from './shared/constants';
import ShareBar, { PctChip } from './shared/ShareBar';
import SectionTitle from './shared/SectionTitle';
import StageFlow from './shared/StageFlow';
import useAppointmentSearch from './shared/useAppointmentSearch';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { buildAppointmentColumns } from './shared/columns';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import ExportButton from './shared/ExportButton';
import PageBanner from './shared/PageBanner';
import AppointmentDelivery from '../B2BOrder/AddAppDelivery';
import MarkMissedModal from './modals/MarkMissedModal';
import CopyIcon from '../../common/CopyIcon';

// Scope notes shown as tooltips. Two kinds of pill: "any status" (All Missed,
// Eventually Delivered) vs "still pending" (everything else — delivered, RTO
// delivered, cancelled, lost, damaged shipments are excluded). The pending
// pills therefore do NOT sum to All Missed; say so instead of letting ops
// discover it by comparing the export row count with the pill counter.
const PENDING_NOTE = 'Still-pending shipments only. Delivered, RTO-delivered, cancelled, lost or damaged shipments are excluded even if they had misses.';
// "Why it was missed" cards: one per closure category, counted on the LATEST miss of each still-open shipment.
const CATEGORY_CARDS = [
    { key: PILL_KEYS.PROZO_FAULT, category: CLOSURE_CATEGORIES.PROZO, icon: <ShieldIcon /> },
    { key: PILL_KEYS.NON_PROZO, category: CLOSURE_CATEGORIES.NON_PROZO, icon: <GroupIcon /> },
    { key: PILL_KEYS.FORCE_MAJEURE, category: CLOSURE_CATEGORIES.FORCE_MAJEURE, icon: <CloudIcon /> },
    { key: PILL_KEYS.JOINT, category: CLOSURE_CATEGORIES.JOINT, icon: <PanToolIcon /> },
].map((c) => ({ ...c, meta: getClosureCategoryMeta(c.category) }));

const MISS_CHIPS = [
    { key: PILL_KEYS.MISS_1, label: '1×' },
    { key: PILL_KEYS.MISS_2, label: '2×' },
    { key: PILL_KEYS.MISS_3_PLUS, label: '3+×', danger: true },
];

const PILLS = [
    { key: PILL_KEYS.ALL, label: 'All Missed',
        info: 'Every shipment with at least 1 missed appointment, in any current status (delivered, RTO, in transit…). This is what the grid and Export This View contain.' },
    { key: PILL_KEYS.MISS_1, label: '1 Miss', info: `Exactly 1 missed appointment. ${PENDING_NOTE}` },
    { key: PILL_KEYS.MISS_2, label: '2 Misses', info: `Exactly 2 missed appointments. ${PENDING_NOTE}` },
    { key: PILL_KEYS.MISS_3_PLUS, label: '3+ Misses', danger: true, info: `3 or more missed appointments. ${PENDING_NOTE}` },
    { key: PILL_KEYS.PROZO_FAULT, label: 'Prozo Fault', info: `Latest miss attributed to Prozo. ${PENDING_NOTE}` },
    { key: PILL_KEYS.NON_PROZO, label: 'Non-Prozo', info: `Latest miss attributed to brand / consignee. ${PENDING_NOTE}` },
    { key: PILL_KEYS.ACTIVE, label: 'Still Active', info: `Has a scheduled next appointment. ${PENDING_NOTE}` },
    { key: PILL_KEYS.NEEDS_SCHEDULING, label: 'Needs Scheduling', info: `No next appointment scheduled yet. ${PENDING_NOTE}` },
    { key: PILL_KEYS.EVENTUALLY_DELIVERED, label: 'Eventually Delivered',
        info: 'Shipments that were missed at least once but have since been delivered.' },
];

export default function MissedRepeat() {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [pill, setPill] = useState(PILL_KEYS.ALL);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);

    const [scheduleRow, setScheduleRow] = useState(null);
    const [missRow, setMissRow] = useState(null);

    // Baseline: at least 1 miss (history count >= 1).
    // Pill filters for MISS_1/MISS_2/MISS_3_PLUS overlap this baseline but use
    // distinct values (eq 1, eq 2, gte 3) so concatenating adds non-conflicting
    // constraints. No dedup needed.
    const baseline = [{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }];
    // "Waiting for 1st appt" shipments have no miss yet, so that stage lists them without the >= 1 miss baseline.
    const extraAndFilter = pill === PILL_KEYS.WAITING_FIRST_APPOINTMENT
        ? buildPillFilter(pill)
        : [...baseline, ...buildPillFilter(pill)];

    const { data: searchData, isFetching } = useAppointmentSearch({
        filters, extraAndFilter, page, sizePerPage, reload,
    });
    const { data: kpi, loading: kpiLoading } = useAppointmentDashboard({
        filters, reload, extraBaseline: baseline, include: ['buckets', 'summary'],
    });
    // Two shipment counts with different scope:
    //   kpi.total   → `all` bucket = baseline (>=1 miss) with NO status
    //                 exclusion. Same filter the grid + export use for the
    //                 "All Missed" pill, so pill label == row count.
    //   openMissed  → miss1 + miss2 + miss3plus, each of which excludes
    //                 delivered / terminal shipments = still-open subset.
    const openMissed = computeTotalMissed(kpi);
    const totalMissed = kpi.total;
    const bucket = (key) => kpi.buckets?.[key] ?? 0;

    // Stage counts outside this page's scope (they have no closed miss yet), so they come from /order/search:
    // only merchant / courier / AWB carry over. Awaiting remark = the Overdue page rule, so both pages match.
    const scopeFilters = { merchant: filters.merchant, courier: filters.courier, awb: filters.awb };
    const todayStart = useMemo(() => dayjs().startOf('day'), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
    const awaitingRemark = useAppointmentSearch({
        filters: scopeFilters, extraAndFilter: buildOverdueFilter(todayStart), page: 0, sizePerPage: 1, reload,
    });
    const waitingFirst = useAppointmentSearch({
        filters: { ...scopeFilters, fromDate: filters.fromDate, toDate: filters.toDate },
        extraAndFilter: WAITING_FIRST_APPOINTMENT_FILTER, page: 0, sizePerPage: 1, reload,
    });

    const counts = {
        [PILL_KEYS.ALL]: totalMissed,
        [PILL_KEYS.MISS_1]: kpi.miss1,
        [PILL_KEYS.MISS_2]: kpi.miss2,
        [PILL_KEYS.MISS_3_PLUS]: kpi.miss3plus,
        [PILL_KEYS.PROZO_FAULT]: kpi.prozoFault,
        [PILL_KEYS.NON_PROZO]: kpi.nonProzo,
        [PILL_KEYS.ACTIVE]: kpi.active,
        [PILL_KEYS.NEEDS_SCHEDULING]: kpi.needsScheduling,
        [PILL_KEYS.EVENTUALLY_DELIVERED]: kpi.eventuallyDelivered,
    };

    const triggerReload = () => setReload((r) => r + 1);
    const navigate = useNavigate();
    const pickPill = (k) => { setPill(k); setPage(0); };

    const stages = [
        {
            key: 'waitingFirst', step: 'Before the cycle', title: 'Waiting for 1st appt', tone: 'grey', icon: <AccessTimeIcon />,
            value: waitingFirst.data?.count ?? 0, loading: waitingFirst.isFetching,
            desc: 'ABD on, 1st appointment not assigned yet', waitingOn: 'client / KAM',
            info: 'ABD shipments in the selected AWB date range with no appointment booked yet and no miss. Not part of the missed total above.',
            onClick: () => pickPill(PILL_KEYS.WAITING_FIRST_APPOINTMENT), selected: pill === PILL_KEYS.WAITING_FIRST_APPOINTMENT,
        },
        {
            key: 'awaitingRemark', step: 'Step 1 · slot passed', title: 'Awaiting remark', tone: 'amber', icon: <EditIcon />,
            value: awaitingRemark.data?.count ?? 0, loading: awaitingRemark.isFetching,
            desc: 'Failed, ops have not given a reason yet', waitingOn: 'Ops',
            info: 'Appointment date has passed and it was never marked as missed. Same list and count as Overdue Appointments (whole book, date range not applied); includes first appointments.',
            // Listed and closed on Overdue, so the box opens that list instead of repeating it here.
            onClick: () => navigate('/appointments/overdue?view=all'),
            link: { to: '/appointments/overdue?view=all', label: 'Close in Overdue' },
        },
        {
            key: 'noNewDate', step: 'Step 2 · reason given', title: 'No new date yet', tone: 'red', icon: <AccessTimeIcon />,
            value: kpi.needsScheduling, loading: kpiLoading,
            desc: 'Waiting for a fresh appointment date', waitingOn: 'client / KAM',
            info: `Marked as missed with a reason, no next appointment booked. ${PENDING_NOTE}`,
            onClick: () => pickPill(PILL_KEYS.NEEDS_SCHEDULING), selected: pill === PILL_KEYS.NEEDS_SCHEDULING,
            link: { to: '/appointments/overdue?view=noNewDate', label: 'View ageing in Overdue' },
        },
        {
            key: 'newDateFixed', step: 'Step 3 · rebooked', title: 'New date fixed', tone: 'blue', icon: <EventAvailableIcon />,
            value: bucket(PILL_KEYS.NEW_DATE_FIXED), loading: kpiLoading,
            desc: 'Next slot booked, waiting for that day', waitingOn: 'delivery day',
            info: `Missed before; the next appointment is booked for today or later. ${PENDING_NOTE}`,
            onClick: () => pickPill(PILL_KEYS.NEW_DATE_FIXED), selected: pill === PILL_KEYS.NEW_DATE_FIXED,
        },
    ];

    const columns = buildAppointmentColumns({
        onSchedule: (row) => setScheduleRow(row),
        onMarkMissed: (row) => setMissRow(row),
    });

    return (
        <>
            <PageTitle>Missed & Repeat Deliveries</PageTitle>
            {isFetching && <Loader />}

            <PageBanner variant='warning' icon={<ReportProblemIcon />} title='What is this page?'>
                Lists all shipments where at least one appointment was missed. Filter by 1, 2, or 3+ missed attempts,
                check whether misses were Prozo&apos;s fault or not, take action on escalations.
                Counts here are <strong>shipments</strong> (one per AWB); the 1 / 2 / 3+ Misses, Prozo and Non-Prozo
                cards and pills count <strong>still-pending</strong> shipments only, so they add up to the Open figure,
                not to All Missed. ABD Analytics counts <strong>missed appointments</strong> (an AWB missed 3 times counts 3),
                so its total is always higher. Hover the <em>i</em> icons for each definition.
            </PageBanner>

            <SectionTitle title='Why it was missed' hint='latest reason given by ops · every open shipment in exactly one card' />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard color='amber' label='Total Missed · Open' value={openMissed} loading={kpiLoading}
                        sub='Missed at least once, not delivered' icon={<LocalShippingIcon />}
                        selected={pill === PILL_KEYS.OPEN_MISSED} onClick={() => pickPill(PILL_KEYS.OPEN_MISSED)}
                        info={`Shipments with at least 1 missed appointment that are still open. ${PENDING_NOTE}`} />
                </Grid>
                {CATEGORY_CARDS.map((c) => {
                    const value = bucket(c.key);
                    return (
                        <Grid item xs={12} sm={6} md={2.4} key={c.key}>
                            <KpiCard color={c.meta.kpiColor} label={c.meta.label} value={value} loading={kpiLoading}
                                valueColor={c.meta.color} icon={c.icon}
                                sub={<><PctChip value={value} total={openMissed} color={c.meta.color} bg={c.meta.bg} />{c.meta.short}</>}
                                selected={pill === c.key} onClick={() => pickPill(c.key)}
                                info={`Open shipments whose latest miss was closed as ${c.meta.label}. ${c.meta.helper}`} />
                        </Grid>
                    );
                })}
            </Grid>
            <ShareBar
                total={openMissed}
                segments={CATEGORY_CARDS.map((c) => ({
                    key: c.key,
                    label: c.meta.label,
                    color: c.meta.color,
                    value: bucket(c.key),
                }))}
            />

            <SectionTitle title='Where they are now' hint='stage in the appointment cycle · click a stage to filter' />
            <StageFlow
                stages={stages}
                loopNote={<>↺ On the new date: delivered → leaves this page · failed again → back to <strong>Awaiting remark</strong> (miss count +1)</>}
                footer={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: '#888' }}>Of the {openMissed} open, missed</Typography>
                        {MISS_CHIPS.map((m) => (
                            <Chip key={m.key} size='small' clickable onClick={() => pickPill(m.key)}
                                label={`${m.label} · ${counts[m.key] ?? 0}`}
                                sx={{
                                    height: 22, fontSize: '10.5px', fontWeight: 700,
                                    bgcolor: m.danger ? '#ffebee' : '#f3f4f6', color: m.danger ? '#c62828' : '#555',
                                    border: pill === m.key ? '1px solid #1565c0' : '1px solid transparent',
                                }} />
                        ))}
                        <Typography sx={{ ml: 'auto', fontSize: '10.5px', color: '#999' }}>click a chip to filter</Typography>
                    </Box>
                )}
            />

            <PillFilters pills={PILLS} value={pill} onChange={(k) => {
                pickPill(k);
                // missCategory adds a "still-pending" exclusion that contradicts
                // delivered-only pills (EVENTUALLY_DELIVERED, DELIVERED) — auto-
                // clear so the grid shows results instead of an empty page.
                if ((k === PILL_KEYS.EVENTUALLY_DELIVERED || k === PILL_KEYS.DELIVERED) && filters.missCategory) {
                    setFilters((f) => ({ ...f, missCategory: '' }));
                }
            }} counts={counts} />

            <FilterBar
                show={['merchant', 'courier', 'attempts', 'missCategory', 'awb', 'dateRange']}
                onApply={(f) => { setFilters(f); setPage(0); triggerReload(); }}
                onReset={() => { setFilters({ fromDate: def.fromDate, toDate: def.toDate }); setPage(0); triggerReload(); }}
            />

            {/* Export uses current filters + currently-selected pill, mirroring the
                grid scope exactly. Server posts the same body shape as /order/search. */}
            <Stack direction='row' justifyContent='flex-end' sx={{ mb: 1.5 }}>
                <ExportButton
                    label='Export This View'
                    filters={filters}
                    extraAndFilter={extraAndFilter}
                    fileName={`ABD_Missed_${pill}`}
                />
            </Stack>

            <ExpandableAppointmentsTable
                columns={columns}
                rows={searchData?.data?.length > 0 ? searchData.data : []}
                page={page}
                sizePerPage={sizePerPage}
                setPage={setPage}
                setSizePerPage={setSizePerPage}
                rowCount={searchData?.totalRecords ?? -1}
                getRowId={(row) => row.id}
            />

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Schedule Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                        <CopyIcon text={scheduleRow?.awb_number} />
                    </Typography>
                }
            >
                <AppointmentDelivery rowData={scheduleRow} setDelConfirm={() => setScheduleRow(null)} setReload={triggerReload} />
            </MUIModal>

            <MUIModal
                open={!!missRow}
                handleClose={() => setMissRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1' sx={{ display: 'flex', alignItems: 'center' }}>
                        Mark as Missed — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{missRow?.awb_number}</Typography>
                        <CopyIcon text={missRow?.awb_number} />
                    </Typography>
                }
            >
                <MarkMissedModal rowData={missRow} onClose={() => setMissRow(null)} onDone={triggerReload}
                    onScheduleNext={(row) => setScheduleRow(row)} />
            </MUIModal>

        </>
    );
}
